import { SearchOutlined } from "@ant-design/icons"
import { Button, Flex, Input, Select, Typography } from "antd"
import React, { useEffect, useState } from "react"

import { SEARCH_DEBOUNCE_MS } from "../../../../../../components/Input/search-debounce.ts"
import { useDebouncedCallback } from "../../../../../../hooks/use-debounced-callback.ts"
import { ProjectLogsDateRangeFilter } from "./ProjectLogsDateRangeFilter.tsx"
import { NO_LOG_FILTERS } from "./project-logs.ts"
import type { ProjectLogFilters, ProjectLogOption } from "./project-logs.types.ts"

const { Text } = Typography

interface ProjectLogsFiltersProps {
  filters: ProjectLogFilters
  actionOptions: ProjectLogOption[]
  typeOptions: ProjectLogOption[]
  userOptions: ProjectLogOption[]
  onChange: (patch: Partial<ProjectLogFilters>) => void
}

function Field({
  label,
  className,
  children
}: {
  label: string
  className?: string
  children: React.ReactNode
}) {
  return (
    <Flex vertical gap={4} className={className}>
      <Text type={"secondary"} className={"text-xs font-medium"}>
        {label}
      </Text>
      {children}
    </Flex>
  )
}

/**
 * Search is kept as local controlled state so Clear Filters can blank it —
 * an uncontrolled `SearchBar` has no way to be reset from outside itself.
 */
export function ProjectLogsFilters({
  filters,
  actionOptions,
  typeOptions,
  userOptions,
  onChange
}: ProjectLogsFiltersProps) {
  const [search, setSearch] = useState(filters.search)
  useEffect(() => setSearch(filters.search), [filters.search])

  const debouncedSearch = useDebouncedCallback(
    (value: string) => onChange({ search: value }),
    SEARCH_DEBOUNCE_MS
  )

  const isFiltered =
    !!filters.search ||
    filters.action !== "all" ||
    filters.type !== "all" ||
    !!filters.user ||
    !!filters.after ||
    !!filters.before

  return (
    <Flex wrap gap={"small"} align={"end"} data-testid={"project-logs-filters"}>
      <Field label={"Search"} className={"w-48 max-md:grow"}>
        <Input
          allowClear
          aria-label={"Search"}
          prefix={<SearchOutlined className={"text-gray-400"} />}
          placeholder={"Search change history"}
          value={search}
          onChange={(e) => {
            setSearch(e.target.value)
            debouncedSearch(e.target.value)
          }}
        />
      </Field>

      <Field label={"Action"} className={"w-32 max-md:grow"}>
        <Select
          aria-label={"Action"}
          value={filters.action}
          onChange={(action) => onChange({ action })}
          options={[{ value: "all", label: "All actions" }, ...actionOptions]}
        />
      </Field>

      <Field label={"Object"} className={"w-32 max-md:grow"}>
        <Select
          aria-label={"Object"}
          value={filters.type}
          onChange={(type) => onChange({ type })}
          options={[{ value: "all", label: "All objects" }, ...typeOptions]}
        />
      </Field>

      <Field label={"User"} className={"w-40 max-md:grow"}>
        <Select
          allowClear
          showSearch
          optionFilterProp={"label"}
          aria-label={"User"}
          placeholder={"All users"}
          value={filters.user ?? undefined}
          onChange={(user) => onChange({ user: user ?? null })}
          options={userOptions}
        />
      </Field>

      <Field label={"Date Range"} className={"w-44 max-md:grow"}>
        <ProjectLogsDateRangeFilter
          after={filters.after}
          before={filters.before}
          onChange={onChange}
        />
      </Field>

      <Button
        type={"link"}
        disabled={!isFiltered}
        onClick={() => {
          setSearch("")
          onChange(NO_LOG_FILTERS)
        }}
      >
        Clear Filters
      </Button>
    </Flex>
  )
}
